/**
 * Custom client adapter for Kubb-generated code.
 *
 * Bridges Kubb's expected client interface to the existing HTTP infrastructure
 * (clientInstance + baseAPICall), preserving:
 *   - Auth headers (Authorization: token {key})
 *   - CSRF token (X-CSRFToken header)
 *   - Sentry error capture (400/500 responses)
 *   - Auto-logout on 401/403 (token removal + page reload)
 *   - Toast notifications (success/error)
 *   - Request duration tracking and timeout detection
 *
 * This file lives outside services/generated/ so it survives `kubb generate --clean`.
 */

import type { AxiosError, ResponseType } from 'axios'
import { clientInstance } from '../utils/http-client'
import { baseAPICall } from './baseAPICall'

// `clientInstance` already has `api/v1` baked into its baseURL, but Kubb
// generates URLs from the OpenAPI spec which includes the full `/api/v1/`
// prefix. Strip it here to avoid ending up with `/api/v1/api/v1/...`.
const API_PATH_V1 = (import.meta.env.VITE_API_PATH_V1 ||
  import.meta.env.VITE_API_PATH ||
  'api/v1') as string
const API_PREFIX_RE = new RegExp(`^/?${API_PATH_V1}/?`)

function stripApiPrefix(url: string): string {
  return url.replace(API_PREFIX_RE, '')
}

export type RequestConfig<TData = unknown> = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  url: string
  data?: TData
  params?: Record<string, unknown>
  headers?: Record<string, string>
  signal?: AbortSignal
  /**
   * Axios response type. Set to `'blob'` or `'arraybuffer'` for binary
   * responses (file downloads, exports). When set to anything non-JSON,
   * the raw response is returned without the normal JSON unwrap.
   */
  responseType?: ResponseType
}

export type ResponseErrorConfig<TError = unknown> = AxiosError<TError>

// Kubb's generated client functions call `client(config)` and then access `.data`
// on the result (when dataReturnType = 'data'). So our client must return { data: T }.
export type ResponseConfig<TData = unknown> = {
  data: TData
}

export type Client = typeof client

async function client<TData, _TError = unknown, TRequest = unknown>(
  config: RequestConfig<TRequest>
): Promise<ResponseConfig<TData>> {
  const isBinary =
    config.responseType === 'blob' || config.responseType === 'arraybuffer'

  if (isBinary) {
    // Binary responses (CSV/XLSX exports, PDF spec sheets) can't flow through
    // baseAPICall because it calls `.data` expecting JSON. Go direct through
    // clientInstance so axios's response.data is the raw Blob/ArrayBuffer.
    const response = await clientInstance.request<TData>({
      method: config.method,
      url: stripApiPrefix(config.url),
      data: config.data,
      params: config.params,
      headers: config.headers,
      signal: config.signal,
      responseType: config.responseType,
    })
    return { data: response.data }
  }

  const data = await baseAPICall<TData>(() =>
    clientInstance.request({
      method: config.method,
      url: stripApiPrefix(config.url),
      data: config.data,
      params: config.params,
      headers: config.headers,
      signal: config.signal,
    })
  )
  return { data }
}

export default client
